// Employee-mode unlock modal. Opened by the hidden activation gestures (five
// quick taps on the header logo, or Ctrl+Shift+G). Two steps:
//
//   1. CODE   — enter a PIN. A personal PIN unlocks immediately; the global
//               enrollment PIN (handed out internally) drops to step 2.
//   2. ENROLL — a not-yet-enrolled rep picks their name and sets a personal
//               PIN (typed twice). Once set, a PIN can only be reset by an
//               admin — never here.
//
// Held in memory only, so an Incognito session always starts locked.

import { useEffect, useState } from "react";

import { api, ApiError } from "../api";
import { useConfigurator } from "../store";
import { FormModal, Field, inputClass } from "./FormModal";

type EnrollRep = { id: number; name: string };

export function EmployeeModal() {
  const { state, unlockEmployee, closeEmployeeModal } = useConfigurator();
  const open = state.employee.modalOpen;

  const [step, setStep] = useState<"code" | "enroll">("code");
  const [code, setCode] = useState("");
  const [reps, setReps] = useState<EnrollRep[]>([]);
  const [repId, setRepId] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time the modal opens so a re-open never shows stale
  // input or a half-finished enrollment step.
  useEffect(() => {
    if (open) {
      setStep("code");
      setCode("");
      setReps([]);
      setRepId("");
      setPin("");
      setPinConfirm("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.employeeAuth(code.trim());
      if (res.token && res.rep) {
        unlockEmployee(res.token, res.rep.name, Boolean(res.rep.is_admin));
        return;
      }
      if (res.enroll) {
        setReps(res.reps ?? []);
        setStep("enroll");
        return;
      }
      setError("Invalid code.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify code.");
    } finally {
      setBusy(false);
    }
  }

  async function submitEnroll(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!repId) {
      setError("Choose your name.");
      return;
    }
    if (pin.length < 4) {
      setError("PIN must be at least 4 digits.");
      return;
    }
    if (pin !== pinConfirm) {
      setError("PINs don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.employeeEnroll({
        rep_id: Number(repId),
        pin,
        pin_confirm: pinConfirm,
      });
      unlockEmployee(res.token, res.rep.name, Boolean(res.rep.is_admin));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not set your PIN.");
    } finally {
      setBusy(false);
    }
  }

  const title = step === "code" ? "Gammill Employee" : "Set up your PIN";
  const subtitle =
    step === "code"
      ? "Enter your PIN to build a quote with employee tools."
      : "First time here — pick your name and choose a PIN to remember.";

  return (
    <FormModal open={open} onClose={closeEmployeeModal} title={title} subtitle={subtitle}>
      {step === "code" ? (
        <form onSubmit={submitCode}>
          <Field label="PIN" error={error ?? undefined}>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={inputClass}
              placeholder="••••"
            />
          </Field>
          <SubmitButton busy={busy} disabled={!code.trim()}>
            {busy ? "Checking…" : "Unlock"}
          </SubmitButton>
        </form>
      ) : (
        <form onSubmit={submitEnroll}>
          {reps.length === 0 ? (
            <p className="mb-4 font-sans text-[14px] text-steel">
              No reps are waiting to enroll. Ask an admin to add you (or reset
              your PIN) first.
            </p>
          ) : (
            <>
              <Field label="Your name" required>
                <select
                  autoFocus
                  value={repId}
                  onChange={(e) => setRepId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select your name…</option>
                  {reps.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Choose a PIN" hint="At least 4 digits." required>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className={inputClass}
                  placeholder="••••"
                />
              </Field>
              <Field label="Confirm PIN" error={error ?? undefined} required>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value)}
                  className={inputClass}
                  placeholder="••••"
                />
              </Field>
              <SubmitButton busy={busy} disabled={!repId || !pin || !pinConfirm}>
                {busy ? "Saving…" : "Set PIN & unlock"}
              </SubmitButton>
              <p className="mt-3 font-sans text-[13px] text-steel">
                Your PIN can't be changed here later — an admin resets it if you
                forget.
              </p>
            </>
          )}
          {reps.length === 0 && error && (
            <p className="mt-1 font-sans text-[14px] text-red-700">{error}</p>
          )}
        </form>
      )}
    </FormModal>
  );
}

function SubmitButton({
  busy,
  disabled,
  children,
}: {
  busy: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="w-full bg-ink px-4 py-3 font-display text-base font-extrabold uppercase tracking-wide text-white transition hover:bg-graphite disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}
